import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Controller, useForm} from 'react-hook-form';
import Input from '../../global/Input';

import {useCleanArray} from '../../hooks/useCleanArray';
import {showMessageOnError, showMessageOnSuccess} from '../../global/CustomToast';
import {TabPanel, TabView} from 'primereact/tabview';
import {Mapping, MappingType, resetMappings} from '../../../redux/slices/MappingSlice';
import {useAppDispatch, useAppSelector} from '../../../redux/hooks';
import {checkPermission, saveMappings} from '../../../functions/functionLibrary';
import {useDeleteMappingMutation, useUpdateMappingMutation} from '../../../redux/rtk/injectedMappingApi';
import EditDialogLayout from '../../global/EditDialogLayout';
import FormPrompt from '../../global/FormPrompt';
import {iterateOverKeys} from '../../functions/Global';
import {Accordion, AccordionTab} from 'primereact/accordion';
import moment from 'moment';
import {useGetBundeslandQuery} from '../../../redux/rtk/injectedIsoBundeslandApi';
import StatusLight from '../../global/StatusLight';
import UnderConstruction from '../../../views/pages/UnderConstruction';
import CautionSign from '../../global/CautionSign';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faDatabase, faInfoCircle, faMapLocation, faProjectDiagram} from '@fortawesome/free-solid-svg-icons';
import {Utilisation} from '../../functions/ProjectMasterData';
import {useCreateLandRegisterMutation, useUpdateLandRegisterMutation} from '../../../redux/rtk/injectedLandregisterApi';
import {
    useCreateUtilisationMutation,
    useGetUtilisationTypesQuery,
    useUpdateUtilisationMutation
} from '../../../redux/rtk/injectedUtilisationApi';
import {useGetPFAPGVKMTypesQuery} from '../../../redux/rtk/injectedMeasureApi';
import {RadioButton} from 'primereact/radiobutton';
import {classNames} from 'primereact/utils';
import {permissions} from '../../../config/permissions';
import {MeasureOverview} from '../measure/MeasureOverview';
import {UtilisationOverview} from './UtilisationOverview';

type Props = {
    id: string,
    projectId: string,
    data: any,
    setIsDirty: (isDirty: boolean) => void
    // eslint-disable-next-line react/no-unused-prop-types
    onFinished: (success: any) => void,
    createNew?: boolean,
    duplicateNew?: boolean,
}
export const UtilisationEditForm = (props: Props): JSX.Element => {
    const {t} = useTranslation(['common', 'stammdaten']);

    const mappings: Mapping[] = useAppSelector((state: any) => state.mappingData.mappings);
    const dispatch = useAppDispatch();

    const [data, setData] = useState<any>();
    const [height, setHeight] = useState<number>(0);
    const [types, setTypes] = useState<any[]>([]);
    const [selectedType, setSelectedType] = useState<any>(undefined);

    const [create, resultCreate] = useCreateUtilisationMutation();
    const [update, resultUpdate] = useUpdateUtilisationMutation();

    const [mappingChanged, setMappingChanged] = useState<boolean>(false);
    const [createNew, setCreateNew] = useState<boolean>(false);
    const [duplicateNew, setDuplicateNew] = useState<boolean>(false);
    const [showUtilisationMapping, setShowUtilisationMapping] = useState<boolean>(false);

    const utilisationTypeQuery = useGetUtilisationTypesQuery({});
    const pfaTypesQuery = useGetPFAPGVKMTypesQuery({});

    const divRef: any = useRef(null);

    // eslint-disable-next-line camelcase
    const isCleaned = useCleanArray(data, {
        flurstueck_id: props.data ? props.data.flurstueck_id : undefined,
        project_id: props.projectId !== '' ? props.projectId : null
    }, true);

    const defaultValues: Utilisation = {
        'project_id': props.data ? props.data.project_id : '',
        'flurstueck_id': props.data ? props.data.flurstueck_id : '',
        'flstkennz': props.data ? props.data.flstkennz : '',
        'inanspruchnahme_id': props.data ? props.data.inanspruchnahme_id : '',
        'inanspruchnahme_type_id': props.data ? props.data.inanspruchnahme_type_id : '',
        'inanspruchnahme_type_name': props.data ? props.data.inanspruchnahme_type_name : '',
        'verzeichnis_lf_nr': props.data ? props.data.verzeichnis_lf_nr : '',
        'verzeichnis_sub_lf_nr': props.data ? props.data.verzeichnis_sub_lf_nr : '',
        'verzeichnis_stand': props.data ? props.data.verzeichnis_stand : '',
        'inanspruchnahme_plan_name': props.data ? props.data.inanspruchnahme_plan_name : '',
        'inanspruchnahme_plan_stand': props.data ? props.data.inanspruchnahme_plan_stand : '',
        'inanspruchnahme_plan_stand_datum': props.data ? props.data.inanspruchnahme_plan_stand_datum : '',
        'pfa_pgv_km_start': props.data ? props.data.pfa_pgv_km_start : '',
        'pfa_pgv_km_ende': props.data ? props.data.pfa_pgv_km_ende : '',
        'flaeche_netto': props.data ? props.data.flaeche_netto : '',
        'flaeche_verzeichnis': props.data ? props.data.flaeche_verzeichnis : '',
        'flaeche_rest': props.data ? props.data.flaeche_rest : '',
        'eco_werte_lf_nr': props.data ? props.data.eco_werte_lf_nr : '',
        'eco_werte_punkte': props.data ? props.data.eco_werte_punkte : '',
        'inanspruchnahme_start_datum_soll': props.data && props.data.inanspruchnahme_start_datum_soll != null ? new Date(props.data.inanspruchnahme_start_datum_soll) : null,
        'inanspruchnahme_start_datum_ist': props.data && props.data.inanspruchnahme_start_datum_ist != null ? new Date(props.data.inanspruchnahme_start_datum_ist) : null,
        'inanspruchnahme_ende_datum_soll': props.data && props.data.inanspruchnahme_ende_datum_soll != null ? new Date(props.data.inanspruchnahme_ende_datum_soll) : null,
        'inanspruchnahme_ende_datum_ist': props.data && props.data.inanspruchnahme_ende_datum_ist != null ? new Date(props.data.inanspruchnahme_ende_datum_ist) : null,
        'inanspruchnahme_massnahme_map_start_datum_soll': props.data && props.data.inanspruchnahme_massnahme_map_start_datum_soll != null ? new Date(props.data.inanspruchnahme_massnahme_map_start_datum_soll) : null,
        'inanspruchnahme_massnahme_map_start_datum_ist': props.data && props.data.inanspruchnahme_massnahme_map_start_datum_ist != null ? new Date(props.data.inanspruchnahme_massnahme_map_start_datum_ist) : null,
        'inanspruchnahme_massnahme_map_ende_datum_soll': props.data && props.data.inanspruchnahme_massnahme_map_ende_datum_soll != null ? new Date(props.data.inanspruchnahme_massnahme_map_ende_datum_soll) : null,
        'inanspruchnahme_massnahme_map_ende_datum_ist': props.data && props.data.inanspruchnahme_massnahme_map_ende_datum_ist != null ? new Date(props.data.inanspruchnahme_massnahme_map_ende_datum_ist) : null,
        'massnahme': props.data ? props.data.massnahme : ''
    };

    const {
        control,
        formState: {errors, isDirty},
        handleSubmit,
        getValues,
        setValue,
        reset,
        watch
    } = useForm({defaultValues});

    let utilisationType = watch('inanspruchnahme_type_id');
    let statusId = 0;

    useEffect(() => {
        if (props.createNew) {
            setCreateNew(props.createNew);
        }
        if (props.duplicateNew) {
            setDuplicateNew(props.duplicateNew);
        }
    }, [props.createNew, props.duplicateNew]);

    useEffect(() => {
        dispatch(resetMappings());

    }, []);

    useEffect(() => {
        props.setIsDirty(isDirty || mappingChanged);
    }, [isDirty, mappingChanged]);

    useEffect(() => {
        if (typeof data !== 'undefined') {
            if (props.data) {
                update(isCleaned).then(async (result: any) => {
                    if (!result.error) {
                        showMessageOnSuccess(t('globalData:global.landRegisterUpdated'), '');
                        props.onFinished(isCleaned);
                        // moveComments(getValues, setValue);
                        reset(undefined, {keepValues: true});
                        setMappingChanged(false);
                    }

                });
            } else {
                create(isCleaned).then(async (result: any) => {
                    if (!result.error) {
                        showMessageOnSuccess(t('globalData:global.landRegisterCreated'), '');
                        props.onFinished(isCleaned);
                        // moveComments(getValues, setValue);
                        reset(undefined, {keepValues: true});
                        setMappingChanged(false);
                    }
                });
            }
        }
    }, [data]);

    useEffect(() => {
        if (divRef.current) {
            setHeight(divRef.current.parentElement.clientHeight - 150);
        }
    }, [divRef.current]);

    const saveData = (data: any, event: any) => {
        console.log(event);
        data = iterateOverKeys(data);
        // console.log(data)
        setData(data);
    };

    const getFormErrorMessage = (name: string) => {
        if (name.indexOf('.') >= 0) {
            const split = name.split('.');

            if (split.length === 3) {
                // @ts-ignore
                if (errors[split[0]] && errors[split[0]][split[1]] && errors[split[0]][split[1]][split[2]]) {
                    // @ts-ignore
                    const text = errors[split[0]][split[1]][split[2]].message;

                    showMessageOnError('', text);
                    return <small className="p-error custom">{text}</small>;
                }
            }
        } else {
            // @ts-ignore
            if (errors[name]) {
                // @ts-ignore
                const text = errors[name].message;

                showMessageOnError('', text);

                return <small className="p-error custom">{text}</small>;
            }
        }
        return undefined;
    };

    // Accordion Headlines --->
    //
    // const getCommentAccordionHeadline = () => {
    //     let right = '';
    //
    //     if (kommentare && kommentare.length > 0) {
    //         right = moment(kommentare[0].zeitstempel).format(t('datetimeformat')) + ': ' +
    //             kommentare[0].kommentar;
    //     }
    //
    //     return (<div className={'grid grid-nogutter'}>
    //         <div
    //             className={'col'}>{t('comments') + (kommentare && kommentare.length > 0 ? ` (${kommentare.length})` : '')}</div>
    //         <div className={'col-auto text-right accordion-addition'}>{right}</div>
    //     </div>);
    // };

    // <--- Accordion Headlines


    const getStatus = () => {
        return (
            <StatusLight id={'statuslight-landregistereditform'} value={statusId} info={'Testinfo'} light={false}
                         horizontal={false} caution={false}/>);
    };

    return (<div className={'relative h-full'}>
        <form ref={divRef} id={props.id} onSubmit={handleSubmit(saveData)} className={'h-full'}>
            <FormPrompt hasUnsavedChanges={isDirty || mappingChanged}/>
            <EditDialogLayout left={<>
                <div className={'h-full'}>
                    <div className={'grid'}>
                        <div className={'col-12 md:col-6 grid grid-nogutter'}>
                            <div className={'col-fixed'}>
                                {getStatus()}
                            </div>
                            <div className={'col pl-2'}>
                                <h5 className={'mb-1'}>{t('type')}</h5>
                                {utilisationTypeQuery.data && utilisationTypeQuery.data.map((item: any) => {
                                    return (<div className={'p-1'}>
                                        <Controller name="inanspruchnahme_type_id" control={control}
                                                    rules={{
                                                        min: {
                                                            value: 1,
                                                            message: t('projectMasterData:measures.columns.zeitraum_type_id') + ' ' + t('input:required')
                                                        },
                                                        required: t('projectMasterData:measures.columns.zeitraum_type_id') + ' ' + t('input:required')
                                                    }}
                                                    render={({field, fieldState}) => (
                                                        <RadioButton
                                                            className={classNames('mr-2', errors['inanspruchnahme_type_id'] ? 'error' : '')}
                                                            value={item.id}
                                                            inputId={'zeitraum_type_id' + item.id}
                                                            name="zeitraum_type_id"
                                                            onChange={(e) => {
                                                                field.onChange(item.id);
                                                            }}
                                                            checked={utilisationType === item.inanspruchnahme_type_id}/>)}/>
                                        <label
                                            htmlFor={'juristisch_type_id_' + item.id}>{item.name}</label>
                                    </div>);
                                })}
                            </div>
                        </div>
                        <div className={'col-12 md:col-6'}>
                            <Input
                                className={'mb-3'}
                                edit={false} label={t('projectMasterData:utilisation.columns.inanspruchnahme_id')}
                                name={'inanspruchnahme_id'}
                                value={props.data ? props.data.land_name : undefined}
                                validationControl={control} validationErrorMessage={getFormErrorMessage}
                                validationRules={{
                                    required: false,
                                }}
                            />
                            <Input
                                edit={false}
                                label={t('projectMasterData:utilisation.columns.inanspruchnahme_type_name')}
                                name={'inanspruchnahme_type_name'}
                                validationControl={control} validationErrorMessage={getFormErrorMessage}
                                validationRules={{
                                    required: false,
                                }}
                            />
                        </div>
                    </div>
                    <hr/>
                    <Accordion>
                        <AccordionTab header={t('projectMasterData:utilisation.columns.verzeichnis')}>
                            <div className={'grid'}>
                                <div className={'col-12 md:col-6'}>
                                    <Input
                                        edit={checkPermission(permissions.editUtilisation)}
                                        label={t('projectMasterData:utilisation.columns.verzeichnis_lf_nr')}
                                        name={'verzeichnis_lf_nr'}
                                        validationControl={control} validationErrorMessage={getFormErrorMessage}
                                        validationRules={{
                                            // required: t('projectMasterData:utilisation.columns.verzeichnis_lf_nr') + ' ' + t('input:required')
                                        }}
                                    />
                                </div>
                                <div className={'col-12 md:col-6'}>
                                    <Input
                                        edit={checkPermission(permissions.editUtilisation)}
                                        label={t('projectMasterData:utilisation.columns.verzeichnis_sub_lf_nr')}
                                        name={'bezirk'}
                                        validationControl={control} validationErrorMessage={getFormErrorMessage}
                                        validationRules={{
                                            // required: t('projectMasterData:utilisation.columns.verzeichnis_sub_lf_nr') + ' ' + t('input:required')
                                        }}
                                    />
                                </div>
                                <div className={'col-12 md:col-6'}>
                                    <Input
                                        edit={checkPermission(permissions.editUtilisation)}
                                        label={t('projectMasterData:utilisation.columns.verzeichnis_stand')}
                                        name={'verzeichnis_stand'}
                                        validationControl={control} validationErrorMessage={getFormErrorMessage}
                                        validationRules={{
                                            // required: t('projectMasterData:utilisation.columns.verzeichnis_stand') + ' ' + t('input:required')
                                        }}
                                    />
                                </div>
                            </div>
                        </AccordionTab>
                    </Accordion>
                    <br/>
                    <Accordion>
                        <AccordionTab header={t('projectMasterData:utilisation.columns.plan')}>
                            <div className={'grid'}>
                                <div className={'col-12 md:col-6'}>
                                    <Input
                                        edit={checkPermission(permissions.editUtilisation)}
                                        label={t('projectMasterData:utilisation.columns.inanspruchnahme_plan_name')}
                                        name={'inanspruchnahme_plan_name'}
                                        validationControl={control} validationErrorMessage={getFormErrorMessage}
                                        validationRules={{
                                            required: false,
                                            // required: true
                                        }}
                                    />
                                </div>
                                <div className={'col-12 md:col-6'}>
                                    <Input edit={checkPermission(permissions.editUtilisation)} name={'type_id'}
                                           label={t('projectMasterData:utilisation.columns.inanspruchnahme_plan_stand')}
                                           validationControl={control} validationErrorMessage={getFormErrorMessage}
                                           validationRules={{
                                               // required: false,
                                               // required: t('projectMasterData:utilisation.columns.inanspruchnahme_plan_stand') + ' ' + t('input:required')
                                           }}
                                           onChange={(e) => {
                                               setSelectedType(types.find((item: any) => item.value === e.value));
                                           }}/>
                                </div>
                                <div className={'col-12 md:col-6'}>
                                    <Input
                                        edit={checkPermission(permissions.editUtilisation)}
                                        label={t('projectMasterData:utilisation.columns.inanspruchnahme_plan_stand_datum')}
                                        name={'inanspruchnahme_plan_stand_datum'}
                                        type={'date'}
                                        showTime={false}
                                        validationControl={control}
                                        validationErrorMessage={getFormErrorMessage}
                                        validationRules={{required: false}}
                                    />
                                </div>
                            </div>
                        </AccordionTab>
                    </Accordion>
                    <br/>
                    <Accordion>
                        <AccordionTab header={t('projectMasterData:utilisation.columns.flaechen')}>
                            <div className={'grid'}>
                                <div className={'col-12 md:col-6'}>
                                    <Input
                                        edit={checkPermission(permissions.editUtilisation)}
                                        label={t('projectMasterData:utilisation.columns.flaeche_netto')}
                                        name={'flaeche_netto'}
                                        validationControl={control}
                                        validationErrorMessage={getFormErrorMessage}
                                        validationRules={{required: false}}
                                    /></div>
                                <div className={'col-12 md:col-6'}>
                                    <Input
                                        edit={checkPermission(permissions.editUtilisation)}
                                        label={t('projectMasterData:utilisation.columns.flaeche_verzeichnis')}
                                        name={'flaeche_verzeichnis'}
                                        validationControl={control}
                                        validationErrorMessage={getFormErrorMessage}
                                        validationRules={{required: false}}
                                    /></div>
                                <div className={'col-12 md:col-6'}>
                                    <Input
                                        edit={checkPermission(permissions.editUtilisation)}
                                        label={t('projectMasterData:utilisation.columns.flaeche_rest')}
                                        name={'flaeche_rest'}
                                        validationControl={control}
                                        validationErrorMessage={getFormErrorMessage}
                                        validationRules={{required: false}}
                                    /></div>
                                {/*<div className={'col-12 md:col-6'}>*/}
                                {/*    <Input*/}
                                {/*        edit={checkPermission(permissions.editUtilisation)}*/}
                                {/*        label={t('projectMasterData:utilisation.columns.eco_werte_lf_nr')}*/}
                                {/*        name={'eco_werte_lf_nr'}*/}
                                {/*        validationControl={control}*/}
                                {/*        validationErrorMessage={getFormErrorMessage}*/}
                                {/*        validationRules={{required: false}}*/}
                                {/*    /></div>*/}
                                {/*<div className={'col-12 md:col-6'}>*/}
                                {/*    <Input*/}
                                {/*        edit={checkPermission(permissions.editUtilisation)}*/}
                                {/*        label={t('projectMasterData:utilisation.columns.eco_werte_punkte')}*/}
                                {/*        name={'eco_werte_punkte'}*/}
                                {/*        validationControl={control}*/}
                                {/*        validationErrorMessage={getFormErrorMessage}*/}
                                {/*        validationRules={{required: false}}*/}
                                {/*    /></div>*/}

                                {/*<div className={'col-12 md:col-6'}>*/}
                                {/*    <Input*/}
                                {/*        edit={checkPermission(permissions.editUtilisation)}*/}
                                {/*        label={t('projectMasterData:utilisation.columns.inanspruchnahme_start_datum_soll')}*/}
                                {/*        name={'inanspruchnahme_start_datum_soll'}*/}
                                {/*        type={'date'}*/}
                                {/*        showTime={false}*/}
                                {/*        validationControl={control}*/}
                                {/*        validationErrorMessage={getFormErrorMessage}*/}
                                {/*        validationRules={{required: false}}*/}
                                {/*    /></div>*/}
                                {/*<div className={'col-12 md:col-6'}>*/}
                                {/*    <Input*/}
                                {/*        edit={checkPermission(permissions.editUtilisation)}*/}
                                {/*        label={t('projectMasterData:utilisation.columns.inanspruchnahme_start_datum_ist')}*/}
                                {/*        name={'inanspruchnahme_start_datum_ist'}*/}
                                {/*        type={'date'}*/}
                                {/*        showTime={false}*/}
                                {/*        validationControl={control}*/}
                                {/*        validationErrorMessage={getFormErrorMessage}*/}
                                {/*        validationRules={{required: false}}*/}
                                {/*    /></div>*/}
                                {/*<div className={'col-12 md:col-6'}>*/}
                                {/*    <Input*/}
                                {/*        edit={checkPermission(permissions.editUtilisation)}*/}
                                {/*        label={t('projectMasterData:utilisation.columns.inanspruchnahme_ende_datum_soll')}*/}
                                {/*        name={'inanspruchnahme_ende_datum_soll'}*/}
                                {/*        type={'date'}*/}
                                {/*        showTime={false}*/}
                                {/*        validationControl={control}*/}
                                {/*        validationErrorMessage={getFormErrorMessage}*/}
                                {/*        validationRules={{required: false}}*/}
                                {/*    /></div>*/}
                                {/*<div className={'col-12 md:col-6'}>*/}
                                {/*    <Input*/}
                                {/*        edit={checkPermission(permissions.editUtilisation)}*/}
                                {/*        label={t('projectMasterData:utilisation.columns.inanspruchnahme_ende_datum_ist')}*/}
                                {/*        name={'inanspruchnahme_ende_datum_ist'}*/}
                                {/*        type={'date'}*/}
                                {/*        showTime={false}*/}
                                {/*        validationControl={control}*/}
                                {/*        validationErrorMessage={getFormErrorMessage}*/}
                                {/*        validationRules={{required: false}}*/}
                                {/*    /></div>*/}
                                {/*<div className={'col-12 md:col-6'}>*/}
                                {/*    <Input*/}
                                {/*        edit={checkPermission(permissions.editUtilisation)}*/}
                                {/*        label={t('projectMasterData:utilisation.columns.inanspruchnahme_massnahme_map_start_datum_soll')}*/}
                                {/*        name={'inanspruchnahme_massnahme_map_start_datum_soll'}*/}
                                {/*        type={'date'}*/}
                                {/*        showTime={false}*/}
                                {/*        validationControl={control}*/}
                                {/*        validationErrorMessage={getFormErrorMessage}*/}
                                {/*        validationRules={{required: false}}*/}
                                {/*    /></div>*/}
                                {/*<div className={'col-12 md:col-6'}>*/}
                                {/*    <Input*/}
                                {/*        edit={checkPermission(permissions.editUtilisation)}*/}
                                {/*        label={t('projectMasterData:utilisation.columns.inanspruchnahme_massnahme_map_start_datum_ist')}*/}
                                {/*        name={'inanspruchnahme_massnahme_map_start_datum_ist'}*/}
                                {/*        type={'date'}*/}
                                {/*        showTime={false}*/}
                                {/*        validationControl={control}*/}
                                {/*        validationErrorMessage={getFormErrorMessage}*/}
                                {/*        validationRules={{required: false}}*/}
                                {/*    /></div>*/}
                                {/*<div className={'col-12 md:col-6'}>*/}
                                {/*    <Input*/}
                                {/*        edit={checkPermission(permissions.editUtilisation)}*/}
                                {/*        label={t('projectMasterData:utilisation.columns.inanspruchnahme_massnahme_map_ende_datum_soll')}*/}
                                {/*        name={'inanspruchnahme_massnahme_map_ende_datum_soll'}*/}
                                {/*        type={'date'}*/}
                                {/*        showTime={false}*/}
                                {/*        validationControl={control}*/}
                                {/*        validationErrorMessage={getFormErrorMessage}*/}
                                {/*        validationRules={{required: false}}*/}
                                {/*    /></div>*/}
                                {/*<div className={'col-12 md:col-6'}>*/}
                                {/*    <Input*/}
                                {/*        edit={checkPermission(permissions.editUtilisation)}*/}
                                {/*        label={t('projectMasterData:utilisation.columns.inanspruchnahme_massnahme_map_ende_datum_ist')}*/}
                                {/*        name={'inanspruchnahme_massnahme_map_ende_datum_ist'}*/}
                                {/*        type={'date'}*/}
                                {/*        showTime={false}*/}
                                {/*        validationControl={control}*/}
                                {/*        validationErrorMessage={getFormErrorMessage}*/}
                                {/*        validationRules={{required: false}}*/}
                                {/*    /></div>*/}
                            </div>
                        </AccordionTab>
                    </Accordion>
                    {/*<Accordion>*/}
                    {/*    <AccordionTab*/}
                    {/*        header={getCommentAccordionHeadline()}>*/}
                    {/*        <CommentForm projectId={props.projectId} t={t} type={'grundbuch'}*/}
                    {/*                     getFormErrorMessage={getFormErrorMessage}*/}
                    {/*                     form={{*/}
                    {/*                         control,*/}
                    {/*                         formState: {errors},*/}
                    {/*                         handleSubmit,*/}
                    {/*                         setValue,*/}
                    {/*                         getValues,*/}
                    {/*                         watch*/}
                    {/*                     }} edit={checkPermission(permissions.editUtilisation)}*/}
                    {/*                     kommentare={kommentare}/>*/}
                    {/*    </AccordionTab>*/}
                    {/*</Accordion>*/}
                    <br/>
                    <Accordion>
                        <AccordionTab header={t('documents')}>
                            <UnderConstruction/>
                        </AccordionTab>
                    </Accordion>
                </div>
            </>} right={<TabView activeIndex={0} renderActiveOnly={false}>
                <TabPanel header={<div><FontAwesomeIcon className="sidebar-icon mr-2"
                                                        icon={faDatabase}/>{t('tabs.relationshipProjectMasterData')}
                </div>}>
                    <br/>
                    <Accordion activeIndex={0}>
                        <AccordionTab header={t('projectMasterData:measures.plural')}>
                            <MeasureOverview
                                projectId={props.projectId}
                                mapping={props.data ? {inanspruchnahme_id: props.data.inanspruchnahme_id} : {}}
                                editable={false}
                                expandable={false}
                                hideGlobalFilter={false}
                                showColumnFilters={true}
                                hideButtons={true}
                                skeletonCount={1}
                                showHeader={false}
                            />
                        </AccordionTab>
                    </Accordion>
                </TabPanel>
            </TabView>}/>
        </form>
    </div>);
};
